<template>
    <App title="Dashboard">
        <div class="dash-page">

            <!-- Top Bar -->
            <div class="dash-topbar">
                <div>
                    <h4 class="dash-page-title">
                        <span class="display-6 fw-semibold">{{ greeting }}</span>
                    </h4>
                </div>
                <div class="dash-topbar-right">
                    <div class="dash-profile-info">
                        <div class="dash-avatar-lg">
                            <span>{{ initials }}</span>
                        </div>
                        <div>
                            <h5 class="dash-profile-name">{{ user.fullname }}</h5>
                            <span class="dash-profile-role">
                                <i class="fa fa-shield-halved"></i>
                                {{ user.role?.replace('_', ' ') }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tab Bar -->
            <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-4">
                <div class="d-flex flex-wrap align-items-center gap-3">
                    <h4 class="dash-section-title mb-0">Overview</h4>
                    <div class="date-badge d-flex align-items-center gap-2">
                        <i class="fa fa-calendar-alt text-muted fs-sm"></i>
                        <span class="fs-sm text-muted">{{ updateDateTime(new Date()) }}</span>
                    </div>
                </div>
                <div class="tab-pill-group d-flex flex-wrap" role="group">
                    <button
                        v-for="tab in TABS"
                        :key="tab.value"
                        class="tab-pill"
                        :class="{ active: activeTab === tab.value }"
                        @click="changeTab(tab.value)"
                    >
                        {{ tab.label }}
                    </button>
                </div>
            </div>

            <!-- Scoped Slot -->
            <slot
                :metrics="metrics"
                :active-tab="activeTab"
                :change-tab="changeTab"
                :chart-transactions="chartTransactions"
                :chart-revenue="chartRevenue"
                :update-date-time="updateDateTime"
                :loading="loading"
                :riders-loading="ridersLoading"
                :operations-loading="operationsLoading"
                :revenue-loading="revenueLoading"
                :assets-loading="assetsLoading"
            />
        </div>
    </App>
</template>

<script lang="ts">
import type Admin from '@/models/Admin'
import App from '@/vue/templates/App.vue'
import { Component, Vue, toNative } from 'vue-facing-decorator'

const TABS = [
    { value: 'today',    label: 'Today'    },
    { value: '7_days',   label: '7 Days'   },
    { value: '30_days',  label: '30 Days'  },
    { value: 'all_time', label: 'All Time' },
] as const

type Period = 'today' | '7_days' | '30_days' | 'all_time'

/**
 * Maps API response keys → local template keys.
 * Only keys that differ between backend and frontend need to be listed.
 * Everything else passes through the spread unchanged.
 */
const METRIC_KEYS: Record<string, string> = {}

function normaliseSeries(raw: any): Array<Record<string, any>> {
    if (!Array.isArray(raw)) return []
    return raw.map(item => ({
        ...item,
        date: new Date(item.date).toISOString(),
    }))
}

function mapMetrics(stats: Record<string, any>): Record<string, any> {
    const result = { ...stats }

    for (const [localKey, apiKey] of Object.entries(METRIC_KEYS)) {
        // If the new payload has the apiKey, use it. 
        // Else keep the existing localKey value. 
        // Else default to 0.
        result[localKey] = stats[apiKey] ?? stats[localKey] ?? 0
        delete result[apiKey]
    }

    result.daily_transactions = normaliseSeries(stats.daily_transactions ?? [])
    result.daily_swaps        = normaliseSeries(stats.daily_swaps ?? [])
    result.daily_revenue      = normaliseSeries(stats.daily_revenue ?? [])

    return result
}

@Component({ components: { App } })
class DashboardSnippet extends Vue {
    readonly TABS = TABS

    ridersLoading     = false
    operationsLoading = false
    revenueLoading    = false
    assetsLoading     = false

    activeTab: Period = 'today'
    metrics: Record<string, any> = mapMetrics({})

    // ── User ─────────────────────────────────────────────────────────────────

    get user(): Admin {
        return this.$user as Admin
    }

    get greeting(): string {
        const h = new Date().getHours()
        if (h < 12) return 'Good morning'
        if (h < 17) return 'Good afternoon'
        return 'Good evening'
    }

    get initials(): string {
        return (this.user?.fullname ?? '')
            .split(' ')
            .slice(0, 2)
            .map(w => w[0]?.toUpperCase() ?? '')
            .join('')
    }

    // ── Loading ───────────────────────────────────────────────────────────────

    get loading(): boolean {
        return this.ridersLoading
            || this.operationsLoading
            || this.revenueLoading
            || this.assetsLoading
    }

    // ── Charts ────────────────────────────────────────────────────────────────

    get chartTransactions(): Array<Record<string, any>> {
        return (this.metrics.daily_transactions as any[] ?? []).map(item => ({
            date:  item.date,
            value: item.swap_transaction_count ?? item.count ?? 0,
        }))
    }

    get chartRevenue(): Array<Record<string, any>> {
        return (this.metrics.daily_revenue as any[] ?? []).map(item => ({
            date:  item.date,
            value: item.total_amount ?? item.amount ?? 0,
        }))
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    updateDateTime(date: Date): string {
        return new Intl.DateTimeFormat('en-US', {
            month: 'long', day: 'numeric', year: 'numeric',
        }).format(date)
    }

    changeTab(period: Period): void {
        this.activeTab = period
        this.fetchAllMetrics()
    }

    // ── Fetching ──────────────────────────────────────────────────────────────

    async fetchAllMetrics(): Promise<void> {
        const params = { params: { period: this.activeTab } }

        await Promise.all([
            this.fetchGroup('riders',     params),
            this.fetchGroup('operations', params),
            this.fetchGroup('revenue',    params),
            this.fetchGroup('assets',     params),
        ])
    }

    private async fetchGroup(
        group: 'riders' | 'operations' | 'revenue' | 'assets',
        params: object
    ): Promise<void> {
        const loadingKey = `${group}Loading` as
            'ridersLoading' | 'operationsLoading' | 'revenueLoading' | 'assetsLoading'

        this[loadingKey] = true

        try {
            const { data } = await this.$http.get(
                this.route(`dashboard.ajax.metrics.${group}`),
                params
            )

            // Merge into shared flat metrics object — children see one source of truth
            this.metrics = mapMetrics({
                ...this.metrics,
                ...data.data,
            })
        } catch (e) {
            console.error(`DashboardSnippet — ${group} fetch error:`, e)
        } finally {
            this[loadingKey] = false
        }
    }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    mounted(): void {
        this.fetchAllMetrics()
    }
}

export default toNative(DashboardSnippet)
</script>

<style lang="scss" scoped>
.dash-page          { padding: 0.5rem 0; }
.dash-section-title { font-size: 1.4rem; font-weight: 700; color: #111827; letter-spacing: -0.3px; }

.date-badge {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 0.35rem 0.75rem;
}

.tab-pill-group {
    display: flex;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 3px;
    gap: 2px;
}

.tab-pill {
    border: none;
    background: transparent;
    padding: 0.3rem 0.9rem;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.15s ease;

    &.active             { background: #3D5A00; color: #fff; }
    &:hover:not(.active) { background: #f0f4e6; color: #3D5A00; }
}

.dash-topbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.25rem;
    gap: 1rem;
}

.dash-page-title  { font-size: 1.1rem; font-weight: 600; color: #111827; margin: 0; }

.dash-topbar-right {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
}

.dash-profile-info {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.dash-avatar-lg {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #3D5A00, #5a7d1a);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.5px;
}

.dash-profile-name {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.15rem;
}

.dash-profile-role {
    font-size: 0.78rem;
    color: #3D5A00;
    font-weight: 500;
    text-transform: capitalize;
    background: #f0f4e6;
    padding: 0.15rem 0.6rem;
    border-radius: 12px;

    i { font-size: 0.68rem; margin-right: 0.2rem; }
}
</style>